import React, { useState, useMemo } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { KeyRound, ArrowLeft, Loader2, CheckCircle2 } from "lucide-react";
import HawkamLogo from "@/components/HawkamLogo";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = useMemo(() => params.get("token") || "", [params]);
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next !== confirm) {
      toast.error("Passwords don't match");
      return;
    }
    if (next.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: next });
      setDone(true);
      setTimeout(() => nav("/admin/login"), 2500);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 md:grid-cols-2 bg-cream" data-testid="reset-password">
      <div className="hidden md:flex flex-col justify-between bg-navy text-cream p-12 relative overflow-hidden">
        <div className="relative z-10">
          <div className="flex items-center gap-3">
            <HawkamLogo size={72} variant="mark" />
            <div>
              <div className="text-2xl font-serif">HAWKAM</div>
              <div className="text-xs uppercase tracking-[0.3em] text-gold">Secure Reset</div>
            </div>
          </div>
          <h2 className="mt-16 text-4xl leading-tight">Choose a new password.</h2>
          <p className="mt-6 text-cream/80 max-w-md text-sm leading-relaxed">
            Pick a strong password — 8 characters or more. Once set, you&apos;ll be redirected to sign in. This reset link is single-use and expires shortly after.
          </p>
        </div>
        <div className="absolute inset-0 opacity-30" style={{ backgroundImage: "radial-gradient(circle at 20% 30%, rgba(201,169,97,0.2), transparent 40%)" }} />
      </div>

      <div className="flex items-center justify-center p-8">
        <form onSubmit={submit} className="w-full max-w-md card-elev p-10">
          <Link to="/admin/login" className="text-xs text-navy/60 hover:text-gold inline-flex items-center gap-1 mb-4">
            <ArrowLeft size={12} /> Back to sign in
          </Link>
          <span className="eyebrow">New password</span>
          <h1 className="mt-2 text-3xl text-navy">Reset password</h1>
          <span className="divider-gold" />

          {!token ? (
            <div className="bg-red-50 border border-red-200 rounded p-4 text-sm text-red-800" data-testid="rp-no-token">
              This reset link is missing its token. Please request a new one from the{" "}
              <Link to="/forgot-password" className="underline decoration-gold underline-offset-4">Forgot password</Link> page.
            </div>
          ) : done ? (
            <div className="bg-emerald-50 border border-emerald-200 rounded p-4 text-sm text-emerald-900 flex items-start gap-3" data-testid="rp-done">
              <CheckCircle2 size={18} className="text-emerald-600 mt-0.5" />
              <div>
                <div className="font-semibold">Password updated.</div>
                <div>Redirecting you to sign in…</div>
              </div>
            </div>
          ) : (
            <>
              <div className="space-y-4">
                <input
                  data-testid="rp-new"
                  type="password"
                  placeholder="New password (8+ chars)"
                  className="field-box"
                  value={next}
                  onChange={(e) => setNext(e.target.value)}
                  minLength={8}
                  required
                />
                <input
                  data-testid="rp-confirm"
                  type="password"
                  placeholder="Confirm new password"
                  className="field-box"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  minLength={8}
                  required
                />
              </div>
              <button
                data-testid="rp-submit"
                type="submit"
                disabled={busy}
                className="btn-gold w-full mt-6 justify-center disabled:opacity-40"
              >
                {busy ? <Loader2 size={14} className="animate-spin" /> : <KeyRound size={14} />}
                {busy ? "Updating…" : "Set new password"}
              </button>
            </>
          )}
        </form>
      </div>
    </div>
  );
}
